import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import withMountGuard from 'hocs/withMountGuard'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyTierBenefitMessageBanner from 'loyalty/components/LoyaltyTierBenefitMessageBanner'
import type { LoyaltyStayUpgradeDetailsModalResult } from 'loyalty/components/StayUpgrade/LoyaltyStayUpgradeDetailsModal'
import LoyaltyStayUpgradeDetailsModal from 'loyalty/components/StayUpgrade/LoyaltyStayUpgradeDetailsModal'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import TextLink from 'LuxKit/TextLink'
import React, { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import OfferPageLoyaltyStayUpgradeCallOutBannerAction from './OfferPageLoyaltyStayUpgradeCallOutBannerAction'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import * as Analytics from 'analytics/analytics'
import styled from 'styled-components'
import { rem } from 'polished'
import useLoyaltyOfferPageTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetStayUpgrade'
import Typography from 'LuxKit/Typography/Typography'
import { useFunnelFeature } from 'checkout/hooks/useFunnelConfig'

/**
 * @NOTE typically you do NOT want to do this but this is a single one-off exception.
 * An AB test has forced a rejig of <LoyaltyTierMessageBanner />. A proper DS solution is required.
 */
const MobileLoyaltyTierBenefitMessageBanner = styled(LoyaltyTierBenefitMessageBanner)`
  padding-inline: ${rem(20)} ${rem(12)} !important;
  min-height: ${rem(56)} !important;
`

/**
 * @NOTE we don't want to usually want to create custom breakpoints like this but product
 * have specifically requested the copy below and it only fits on mid mobile sizes up.
 */
const ShowOnlySmallMobileBreakpoint = styled.div`
  @media screen and (min-width: 405px) {
    display: none;
  }
`

const ShowMidMobileUpBreakpoint = styled.div`
  @media screen and (max-width: 404.98px) {
    display: none;
  }
`

interface Props {
  packageView: App.OfferPackageOptionView
}

function OfferPageLoyaltyStayUpgradeCallOutBanner({ packageView }: Props) {
  const loyaltyUpgradeAvailability = packageView.loyaltyUpgradeAvailability
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const showModal = useContext(ModalContext)
  const { offer, checkInDate, checkOutDate } = useContext(OfferPageStateContext)
  const { selectedTargetUpgradePackage } = useLoyaltyOfferPageTargetStayUpgrade(packageView.package)
  const intl = useIntl()

  // Funnel Optimiser - check if room upgrade is enabled
  const showRoomUpgrade = useFunnelFeature('roomUpgrade')

  const freeUpgradeAvailableLabel = intl.formatMessage({
    id: 'eCsJ1tqQgS',
    defaultMessage: 'Free upgrade available',
    description: 'Banner text when free room or package upgrade is available for loyalty members',
  })

  const freeUpgradeAppliedLabel = intl.formatMessage({
    id: 'FXAWsuIgHy',
    defaultMessage: 'Free upgrade applied!',
    description: 'Banner text when free room or package upgrade has been applied for loyalty members',
  })

  const freeRoomUpgradeLabel = intl.formatMessage({
    id: 'PQma7939Ol',
    defaultMessage: 'Free room upgrade',
    description: 'Banner text for free room upgrade option on small mobile screens',
  })

  const viewUpgradedRoomLabel = intl.formatMessage({
    id: '+NdrS08mHP',
    defaultMessage: 'View upgraded room',
    description: 'Link text to view details of the upgraded room for loyalty members',
  })

  const freePackageUpgradeLabel = intl.formatMessage({
    id: 'F8xz2jMv7F',
    defaultMessage: 'Free package upgrade',
    description: 'Banner text for free package upgrade option on small mobile screens',
  })

  const viewUpgradedInclusionsLabel = intl.formatMessage({
    id: 'GBIBXhiOpv',
    defaultMessage: 'View upgraded inclusions',
    description: 'Link text to view details of the upgraded package inclusions for loyalty members',
  })

  const applyFreeRoomUpgradeLabel = intl.formatMessage({
    id: '/2IZLWlxN5',
    defaultMessage: 'Apply free room upgrade',
    description: 'Banner text to apply free room upgrade for loyalty members',
  })

  const applyFreePackageUpgradeLabel = intl.formatMessage({
    id: '66neYNPnXP',
    defaultMessage: 'Apply free package upgrade',
    description: 'Banner text to apply free package upgrade for loyalty members',
  })

  const freeRoomOrPackageUpgradeLabel = intl.formatMessage({
    id: 'QDRjijSKrp',
    defaultMessage: 'Free room or package upgrade',
    description: 'Banner text when both room and package upgrades are available for loyalty members on desktop',
  })

  const { toggleTargetStayUpgrade, selectedTargetUpgradeType } = useLoyaltyOfferPageTargetStayUpgrade(packageView.package)
  const applyUpgrade = useCallback((upgradeType: App.LoyaltyStayUpgradeType | undefined) => {
    toggleTargetStayUpgrade(upgradeType)
    Analytics.trackClientEvent({
      subject: `loyalty_${upgradeType}_upgrade`,
      action: upgradeType ? 'toggled_on' : 'toggled_off',
      category: 'offer_page',
      type: 'interaction',
    })
  }, [toggleTargetStayUpgrade])

  const showTargetUpgradeRoomDetails = useCallback(async() => {
    let result: LoyaltyStayUpgradeDetailsModalResult | undefined

    switch (loyaltyUpgradeAvailability?.upgradeType) {
      case 'room-only':
        result = await showModal(<LoyaltyStayUpgradeDetailsModal
          upgradeType="room"
          originPackage={packageView.package}
          targetPackage={loyaltyUpgradeAvailability.roomUpgradePackage}
          offer={offer}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          hasApplyAction
          hasRemoveAction={selectedTargetUpgradeType === 'room'}
        />)

        break
      case 'package-only':
        result = await showModal(<LoyaltyStayUpgradeDetailsModal
          upgradeType="package"
          originPackage={packageView.package}
          targetPackage={loyaltyUpgradeAvailability.packageUpgradePackage}
          offer={offer}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          hasApplyAction
          hasRemoveAction={selectedTargetUpgradeType === 'package'}
        />)

        break
    }

    if (result) {
      applyUpgrade(result.upgradeType)
    }
  }, [applyUpgrade, checkInDate, checkOutDate, loyaltyUpgradeAvailability, offer, packageView, selectedTargetUpgradeType, showModal])

  // Don't show room upgrade if funnel config disables it or if no upgrade available
  if (!showRoomUpgrade || !loyaltyUpgradeAvailability) return null

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <CSSBreakpoint only="mobile">
      <MobileLoyaltyTierBenefitMessageBanner
        tier={tier}
        benefitType="hotel_upgrade"
        endContent={<OfferPageLoyaltyStayUpgradeCallOutBannerAction packageView={packageView} />}
        density="compact"
        icon={false}
      >
        <Group direction="vertical" wrap="wrap">
          {loyaltyUpgradeAvailability.upgradeType === 'room-and-package' && !selectedTargetUpgradePackage && <>{freeUpgradeAvailableLabel}</>}
          {loyaltyUpgradeAvailability.upgradeType === 'room-and-package' && !!selectedTargetUpgradePackage && <>{freeUpgradeAppliedLabel}</>}
          <ShowOnlySmallMobileBreakpoint>
            {loyaltyUpgradeAvailability.upgradeType === 'room-only' && <>
              <Typography wrap="no-wrap">{freeRoomUpgradeLabel}</Typography>
              <TextLink weight="regular" size="caption" onClick={showTargetUpgradeRoomDetails}>{viewUpgradedRoomLabel}</TextLink>
            </>}
            {loyaltyUpgradeAvailability.upgradeType === 'package-only' && <>
              <Typography wrap="no-wrap">{freePackageUpgradeLabel}</Typography>
              <TextLink weight="regular" size="caption" onClick={showTargetUpgradeRoomDetails}>{viewUpgradedInclusionsLabel}</TextLink>
            </>}
          </ShowOnlySmallMobileBreakpoint>
          <ShowMidMobileUpBreakpoint>
            {loyaltyUpgradeAvailability.upgradeType === 'room-only' && <>
              <Typography wrap="no-wrap">{applyFreeRoomUpgradeLabel}</Typography>
              <TextLink weight="regular" size="caption" onClick={showTargetUpgradeRoomDetails}>{viewUpgradedRoomLabel}</TextLink>
            </>}
            {loyaltyUpgradeAvailability.upgradeType === 'package-only' && <>
              <Typography wrap="no-wrap">{applyFreePackageUpgradeLabel}</Typography>
              <TextLink weight="regular" size="caption" onClick={showTargetUpgradeRoomDetails}>{viewUpgradedInclusionsLabel}</TextLink>
            </>}
          </ShowMidMobileUpBreakpoint>
        </Group>
      </MobileLoyaltyTierBenefitMessageBanner>
    </CSSBreakpoint>
    <CSSBreakpoint min="tablet">
      <LoyaltyTierBenefitMessageBanner
        tier={tier}
        benefitType="hotel_upgrade"
        endContent={<OfferPageLoyaltyStayUpgradeCallOutBannerAction packageView={packageView} />}
      >
        <Group direction="horizontal" gap="0 24" wrap="wrap">
          {loyaltyUpgradeAvailability.upgradeType === 'room-and-package' && <div>{freeRoomOrPackageUpgradeLabel}</div>}
          {loyaltyUpgradeAvailability.upgradeType === 'room-only' && <>
            <Group direction="horizontal" gap={8}>
              <div>{intl.formatMessage(
                {
                  id: '9OPyw89ScA',
                  defaultMessage: 'Apply free room upgrade to <bold>{roomName}</bold>',
                  description: 'Banner text to apply free room upgrade to a specific room name for loyalty members on desktop',
                },
                {
                  roomName: loyaltyUpgradeAvailability.roomUpgradePackage.roomType?.name ?? loyaltyUpgradeAvailability.roomUpgradePackage.name,
                  bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
                },
              )}
              </div>
            </Group>
            <TextLink size="caption" onClick={showTargetUpgradeRoomDetails}>{viewUpgradedRoomLabel}</TextLink>
          </>}
          {loyaltyUpgradeAvailability.upgradeType === 'package-only' && <>
            <div>{applyFreePackageUpgradeLabel}</div>
            <TextLink size="caption" onClick={showTargetUpgradeRoomDetails}>{viewUpgradedInclusionsLabel}</TextLink>
          </>}
        </Group>
      </LoyaltyTierBenefitMessageBanner>
    </CSSBreakpoint>
  </ProductPaletteProvider>
}

export default withMountGuard(() => useAppSelector(getIsCurrentRegionUserLoyaltyRegion))(
  withOfferPageState((state) => ({
    offer: state.offer,
  }), OfferPageLoyaltyStayUpgradeCallOutBanner),
)
